import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, CircularProgress, Alert, Chip
} from '@mui/material';
import DevicesIcon from '@mui/icons-material/Devices';
import dayjs from 'dayjs';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const ActiveSessions = () => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchSessions = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('jwtToken');
            const res = await axios.get(`${API_BASE_URL}/api/admin/active-sessions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSessions(res.data || []);
        } catch (err) {
            setError('Failed to fetch active sessions.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSessions();
        const interval = setInterval(fetchSessions, 15000); // refresh every 15s
        return () => clearInterval(interval);
    }, []);

    const formatUserAgent = (ua) => {
        if (!ua) return 'Unknown Device';
        if (ua.includes('iPhone') || ua.includes('Android')) return 'Mobile Device';
        if (ua.includes('Chrome')) return 'Chrome Browser';
        if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari Browser';
        if (ua.includes('Firefox')) return 'Firefox Browser';
        if (ua.includes('Edge')) return 'Edge Browser';
        return 'Desktop Browser';
    };

    if (loading && sessions.length === 0) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#11224d', fontWeight: 'bold' }}>
                {sessions.length} User{sessions.length !== 1 ? 's' : ''} Active Now
            </Typography>
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#11224d' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email / Gmail</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Company</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Role</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Device</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>IP Address</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Logged In Since</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sessions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">No active sessions found.</TableCell>
                            </TableRow>
                        ) : (
                            sessions.map((session) => (
                                <TableRow key={session.id} hover>
                                    <TableCell>{session.email}</TableCell>
                                    <TableCell>{session.company_name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={session.role}
                                            size="small"
                                            sx={{
                                                textTransform: 'capitalize',
                                                fontSize: '0.65rem',
                                                height: '20px',
                                                backgroundColor:
                                                    session.role === 'admin' ? '#f3e5f5' :
                                                    session.role === 'shipper' ? '#c8e6c9' : 
                                                    session.role === 'carrier' ? '#fff9c4' : 
                                                    session.role === 'broker'  ? '#bbdefb' : '#e0e0e0',
                                                color:
                                                    session.role === 'admin' ? '#7b1fa2' :
                                                    session.role === 'shipper' ? '#1b5e20' : 
                                                    session.role === 'carrier' ? '#f57f17' : 
                                                    session.role === 'broker'  ? '#0d47a1' : '#000000',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DevicesIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                                            <Typography variant="body2">{formatUserAgent(session.user_agent)}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{session.ip_address || 'Unknown'}</TableCell>
                                    <TableCell>{dayjs(session.last_active).format('MMM D, YYYY h:mm A')}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ActiveSessions;